import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SettingsCard } from "../../SettingsCard";
import { EditableCardFooter } from "../../EditableCardFooter";
import { ApiError } from "../../../../services/api.errors";
import { PermissionCategory, Role, RoleDetail, RolesAPI } from "../../../../services/roles.service";
import { RoleList } from "./RoleList";
import { RolePermissionChecklist } from "./RolePermissionChecklist";
import { CreateRoleModal } from "./CreateRoleModal";
import { diffPermissions, summarizeChanges } from "./permissionDiff";
import { useRole } from "../../../../hooks/useRole";
import { actionErrorMessage } from "../../../../utils/network";

export const RoleCatalogCard: React.FC = () => {
  // Editing a role needs roles.manage, which is a SEPARATE code from
  // settings_access.edit — the page grant only gets you in to look. Without this
  // check the Edit button renders for someone who cannot save, so they untick
  // permissions, hit Save, and get a 403 with no way to undo from this screen.
  // Legacy fallback: Admin, matching LEGACY_ROLE_GRANTS.
  const { canOr, isAdmin } = useRole();
  const canManageRoles = canOr("roles.manage", isAdmin);
  const [catalog, setCatalog] = useState<PermissionCategory[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());

  const [modalMode, setModalMode] = useState<{ open: boolean; cloneFrom: Role | null }>({ open: false, cloneFrom: null });

  useEffect(() => {
    Promise.allSettled([RolesAPI.getPermissionCatalog(), RolesAPI.listRoles()]).then(([catRes, rolesRes]) => {
      if (catRes.status === "fulfilled") setCatalog(catRes.value);
      else toast.error("Failed to load the permission catalog.");
      if (rolesRes.status === "fulfilled") {
        setRoles(rolesRes.value);
        // Land on one of the clinic's own editable roles, not a read-only
        // system template - Admin first since that's the role every clinic
        // is guaranteed to have (see provision_clinic_roles).
        const ownRoles = rolesRes.value.filter(r => !r.is_system);
        const initial = ownRoles.find(r => r.template_key === "admin") ?? ownRoles[0] ?? rolesRes.value[0];
        if (initial) setSelectedId(initial.role_id);
      } else {
        toast.error("Failed to load roles.");
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    setEditing(false);
    RolesAPI.getRoleDetail(selectedId)
      .then(d => { setDetail(d); setDraft(new Set(d.permission_codes)); })
      .catch(() => toast.error("Failed to load role permissions."))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const selectedRole = roles.find(r => r.role_id === selectedId) ?? null;

  const handleSave = async () => {
    if (!selectedId || !detail) return;
    setSaving(true);
    // Snapshot what the role actually had before the round trip — the toast
    // must describe the change the server accepted, not the local draft.
    const before = new Set(detail.permission_codes);
    try {
      const updated = await RolesAPI.setRolePermissions(selectedId, Array.from(draft));
      setDetail(updated);
      setDraft(new Set(updated.permission_codes));
      setEditing(false);

      const changes = diffPermissions(catalog, before, new Set(updated.permission_codes));
      if (changes.length === 0) {
        toast.info(`No permission changes for ${updated.name}.`);
      } else {
        for (const line of summarizeChanges(updated.name, changes)) {
          toast.success(line);
        }
        // Saying "Doctor can now edit X" is misleading if no Doctor exists to
        // receive it — the grant is stored but affects nobody.
        if (updated.assigned_user_count === 0) {
          toast.warning(`Nobody holds the ${updated.name} role yet — assign it for this to take effect.`);
        }
      }
    } catch (err) {
      toast.error(
        actionErrorMessage(
          err, err instanceof ApiError ? err.message : "Failed to save permissions.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (detail) setDraft(new Set(detail.permission_codes));
    setEditing(false);
  };

  const handleRoleCreated = (created: RoleDetail) => {
    setRoles(prev => [...prev, created]);
    setSelectedId(created.role_id);
  };

  if (loading) {
    return <div className="text-sm text-gray-400 py-10 text-center">Loading…</div>;
  }

  return (
    <SettingsCard
      title="Role Catalog"
      subtitle="Create custom roles or edit what each role can do"
      icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3 6 6 1-4.5 4.5L17 20l-5-3-5 3 1.5-6.5L4 9l6-1z"/>
        </svg>
      }
      footer={
        selectedRole && !selectedRole.is_system && !canManageRoles ? (
          <p className="text-xs text-gray-400">
            You can view roles but not change them — that needs the
            &quot;Manage Roles &amp; Permissions&quot; grant.
          </p>
        ) : selectedRole && !selectedRole.is_system ? (
          <EditableCardFooter
            editing={editing}
            saving={saving}
            onEdit={() => setEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
            saveLabel="Save Permissions"
          />
        ) : selectedRole?.is_system ? (
          <p className="text-xs text-gray-400">
            System templates are read-only - clone &quot;{selectedRole.name}&quot; to create an editable copy.
          </p>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
        <RoleList
          roles={roles}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCreateNew={() => setModalMode({ open: true, cloneFrom: null })}
          onClone={role => setModalMode({ open: true, cloneFrom: role })}
          canManage={canManageRoles}
        />
        <div>
          {detailLoading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading permissions…</div>
          ) : !detail ? (
            <p className="text-sm text-gray-400 py-8 text-center">Select a role to see its permissions.</p>
          ) : (
            <>
              {/* Who this role actually applies to. Without it, editing a role
                  nobody holds looks identical to editing one the whole clinic
                  holds — you save, get a success toast, and nothing changes for
                  any real user. */}
              {!detail.is_system && (
                detail.assigned_user_count === 0 ? (
                  <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="text-amber-500 flex-shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p className="text-xs text-amber-800">
                      No staff member has the {detail.name} role yet, so changes here won&apos;t affect
                      anyone. Assign it from <span className="font-medium">Access Control &amp; Permissions
                      → View All → Roles</span>.
                    </p>
                  </div>
                ) : (
                  <p className="mb-3 text-xs text-gray-400">
                    Applies to {detail.assigned_user_count} staff member
                    {detail.assigned_user_count === 1 ? "" : "s"}.
                  </p>
                )
              )}
              <RolePermissionChecklist
                catalog={catalog}
                value={editing ? draft : new Set(detail.permission_codes)}
                onChange={setDraft}
                readOnly={!editing}
              />
            </>
          )}
        </div>
      </div>

      <CreateRoleModal
        isOpen={modalMode.open}
        cloneFrom={modalMode.cloneFrom}
        onClose={() => setModalMode({ open: false, cloneFrom: null })}
        onCreated={handleRoleCreated}
      />
    </SettingsCard>
  );
};
